import { supabase } from '@/lib/supabase'
import type { Tables } from '@/types/database.types'
import { listDays } from '@/services/dayService'

export type Trip = Tables<'trips'>
export type TripStatus = 'active' | 'on_hold' | 'archived'

export type TripCurrency = 'EUR' | 'USD' | 'GBP' | 'CHF' | 'JPY' | 'CAD'

export interface NewTripInput {
  title: string
  destination?: string
  startDate: string
  endDate: string
  coverUrl?: string
  currency: TripCurrency
}

export interface UpdateTripInput {
  title: string
  destination?: string
  startDate: string
  endDate: string
  coverUrl?: string
  currency: TripCurrency
}

export class DateRangeConflictError extends Error {
  affectedDayCount: number
  affectedActivityCount: number

  constructor(affectedDayCount: number, affectedActivityCount: number) {
    super('Some days being removed from the date range still have activities.')
    this.name = 'DateRangeConflictError'
    this.affectedDayCount = affectedDayCount
    this.affectedActivityCount = affectedActivityCount
  }
}

function enumerateDates(startDate: string, endDate: string): string[] {
  const dates: string[] = []
  const cursor = new Date(`${startDate}T00:00:00Z`)
  const end = new Date(`${endDate}T00:00:00Z`)
  while (cursor <= end) {
    dates.push(cursor.toISOString().slice(0, 10))
    cursor.setUTCDate(cursor.getUTCDate() + 1)
  }
  return dates
}

export async function listTrips(): Promise<Trip[]> {
  const { data, error } = await supabase
    .from('trips')
    .select('*')
    .neq('status', 'archived')
    .order('start_date', { ascending: false })
  if (error) throw error
  return data
}

export async function listArchivedTrips(): Promise<Trip[]> {
  const { data, error } = await supabase
    .from('trips')
    .select('*')
    .eq('status', 'archived')
    .order('start_date', { ascending: false })
  if (error) throw error
  return data
}

export async function createTrip(input: NewTripInput): Promise<Trip> {
  const { data, error } = await supabase.rpc('create_trip_with_owner', {
    p_title: input.title,
    p_destination: input.destination ?? '',
    p_start_date: input.startDate,
    p_end_date: input.endDate,
    p_cover_url: input.coverUrl ?? '',
    p_currency: input.currency,
  })
  if (error) throw error
  return data
}

export async function updateTripStatus(tripId: string, status: TripStatus): Promise<void> {
  const { error } = await supabase.from('trips').update({ status }).eq('id', tripId)
  if (error) throw error
}

export async function updateTrip(
  tripId: string,
  input: UpdateTripInput,
  confirmUnassign = false,
): Promise<Trip> {
  const currentDays = await listDays(tripId)
  const newDates = new Set(enumerateDates(input.startDate, input.endDate))
  const daysToRemove = currentDays.filter((day) => !newDates.has(day.date))

  if (daysToRemove.length > 0) {
    const dayIdsToRemove = daysToRemove.map((day) => day.id)
    const { count, error: countError } = await supabase
      .from('activities')
      .select('id', { count: 'exact', head: true })
      .in('day_id', dayIdsToRemove)
    if (countError) throw countError

    if (count && count > 0) {
      if (!confirmUnassign) {
        throw new DateRangeConflictError(daysToRemove.length, count)
      }
      const { error: unassignError } = await supabase
        .from('activities')
        .update({ day_id: null })
        .in('day_id', dayIdsToRemove)
      if (unassignError) throw unassignError
    }

    const { error: deleteError } = await supabase.from('days').delete().in('id', dayIdsToRemove)
    if (deleteError) throw deleteError
  }

  const existingDates = new Set(currentDays.map((day) => day.date))
  const datesToAdd = [...newDates].filter((date) => !existingDates.has(date))
  if (datesToAdd.length > 0) {
    const { error: insertError } = await supabase
      .from('days')
      .insert(datesToAdd.map((date) => ({ trip_id: tripId, date })))
    if (insertError) throw insertError
  }

  const { data, error } = await supabase
    .from('trips')
    .update({
      title: input.title,
      destination: input.destination,
      start_date: input.startDate,
      end_date: input.endDate,
      cover_url: input.coverUrl,
      currency: input.currency,
    })
    .eq('id', tripId)
    .select()
    .single()
  if (error) throw error
  return data
}

export async function getTrip(tripId: string): Promise<Trip> {
  const { data, error } = await supabase.from('trips').select('*').eq('id', tripId).single()
  if (error) throw error
  return data
}

export async function deleteTrip(tripId: string): Promise<void> {
  const { error } = await supabase.from('trips').delete().eq('id', tripId)
  if (error) throw error
}

export async function countTripMembers(tripId: string): Promise<number> {
  const { count, error } = await supabase
    .from('trip_members')
    .select('*', { count: 'exact', head: true })
    .eq('trip_id', tripId)
  if (error) throw error
  return count ?? 1
}

export type TripMemberRole = 'owner' | 'companion' | 'viewer'

export interface TripMemberWithEmail {
  user_id: string
  email: string | null
  role: TripMemberRole
  created_at: string
}

export async function listTripMembersWithEmail(tripId: string): Promise<TripMemberWithEmail[]> {
  const { data, error } = await supabase.rpc('list_trip_members_with_email', {
    p_trip_id: tripId,
  })
  if (error) throw error
  return data as TripMemberWithEmail[]
}

export async function getMyRole(tripId: string): Promise<TripMemberRole | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) return null
  const { data, error } = await supabase
    .from('trip_members')
    .select('role')
    .eq('trip_id', tripId)
    .eq('user_id', user.id)
    .maybeSingle()
  if (error) throw error
  return (data?.role as TripMemberRole | undefined) ?? null
}

export async function updateMemberRole(
  tripId: string,
  userId: string,
  role: 'companion' | 'viewer',
): Promise<void> {
  const { error } = await supabase
    .from('trip_members')
    .update({ role })
    .eq('trip_id', tripId)
    .eq('user_id', userId)
  if (error) throw error
}

export async function removeMember(tripId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('trip_members')
    .delete()
    .eq('trip_id', tripId)
    .eq('user_id', userId)
  if (error) throw error
}

export async function enableTripSharing(tripId: string): Promise<string> {
  const { data, error } = await supabase.rpc('enable_trip_sharing', { p_trip_id: tripId })
  if (error) throw error
  return data
}

export async function disableTripSharing(tripId: string): Promise<void> {
  const { error } = await supabase.rpc('disable_trip_sharing', { p_trip_id: tripId })
  if (error) throw error
}
